import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/** カテゴリ1本分の最小幅（px）。これ未満に潰れないよう、必要なら横スクロール */
export const BAR_CHART_MIN_PX_PER_CATEGORY = 70;

/**
 * 表示件数がこの値以下のとき、横スクロールなし・ラベル常時表示とし、拡大/全体メニューを出さない。
 */
export const BAR_CHART_WIDTH_MENU_THRESHOLD = 7;

export type BarChartScrollLayout = 'expand' | 'fit';

export type ChartDisplayConfig = {
    /** 実際に適用される表示モード */
    effectiveViewMode: 'fit' | 'expand';
    /** 拡大/全体メニューを表示するかどうか */
    showViewModeMenu: boolean;
    /** 横スクロールのレイアウト（effectiveViewMode と同値） */
    scrollLayout: BarChartScrollLayout;
    /** 棒上のラベルを表示するかどうか（expand 時のみ true） */
    showBarLabels: boolean;
    /** X軸の日付を間引くかどうか（fit 時のみ true） */
    thinXAxis: boolean;
    /** 月またぎの省略（直前の日付との比較で「日」のみ表示）を使うか */
    useMonthAwareLabels: boolean;
};

/**
 * グラフ種別・データ件数・デバイス幅・ユーザー選択モードから、グラフの表示条件を決定する。
 */
export function getChartDisplayConfig(
    chartType: 'ratio' | 'transition',
    labelCount: number,
    isSmUpViewport: boolean,
    userViewMode: 'fit' | 'expand',
): ChartDisplayConfig {
    const isMobile = !isSmUpViewport;
    const isShortList = labelCount <= BAR_CHART_WIDTH_MENU_THRESHOLD;

    // 1. 実際の表示モードを決定
    let effectiveViewMode: 'fit' | 'expand';
    if (chartType === 'ratio') {
        if (isMobile) {
            // スマホの比率は常に拡大（スクロール・ラベルあり）
            effectiveViewMode = 'expand';
        } else {
            // PCの比率は、7日以下なら拡大、8日以上ならユーザー選択
            effectiveViewMode = isShortList ? 'expand' : userViewMode;
        }
    } else {
        // 推移の場合
        if (isShortList) {
            // 7日以下の場合、スマホは全体（fit）、PCは拡大（expand）
            // ※スマホで7日以下の推移は、画面幅に収まるため fit とするが、
            // X軸の間引きは行わない（後述の thinXAxis で制御）
            effectiveViewMode = isMobile ? 'fit' : 'expand';
        } else {
            // 8日以上の場合はユーザー選択
            effectiveViewMode = userViewMode;
        }
    }

    // 2. メニューを表示するかどうか
    // 7日以下の場合はメニューを出さない。
    // スマホの比率（常に拡大）の場合もメニューを出さない。
    let showViewModeMenu = !isShortList;
    if (chartType === 'ratio' && isMobile) {
        showViewModeMenu = false;
    }

    // 3. 各種レイアウトフラグ
    const scrollLayout = effectiveViewMode;
    const showBarLabels = effectiveViewMode === 'expand';
    
    // 4. X軸の間引き
    // 基本は fit モードのときに間引くが、7日以下の場合は間引かない（全日付表示）
    const thinXAxis = effectiveViewMode === 'fit' && !isShortList;

    // 5. 月またぎの省略（Month-aware labels）
    // スクロールが発生するスマホの拡大表示では、スクロール時に月が分からなくなるのを防ぐため省略しない。
    // それ以外（PC、またはスマホの全体表示）では省略を有効にする。
    let useMonthAwareLabels = true;
    if (isMobile && scrollLayout === 'expand') {
        useMonthAwareLabels = false;
    }

    return {
        effectiveViewMode,
        showViewModeMenu,
        scrollLayout,
        showBarLabels,
        thinXAxis,
        useMonthAwareLabels,
    };
}

type UseBarChartScrollContainerOptions = {
    /** expand: 最小幅を確保して横スクロール可。fit: コンテナ幅に合わせ横スクロールなし */
    layout?: BarChartScrollLayout;
};

/**
 * 棒グラフ・積み上げ棒用: 親幅とデータ本数からチャート内幅（px）を決める。
 */
export function useBarChartScrollContainer(
    labelCount: number,
    options?: UseBarChartScrollContainerOptions,
): {
    scrollRef: RefObject<HTMLDivElement | null>;
    /** 内側ラッパーに指定する width（px） */
    innerWidthPx: number;
    /** ResizeObserver で得たスクロール領域の幅（初回前は 0） */
    containerWidth: number;
    /** layout が expand のとき labelCount * BAR_CHART_MIN_PX_PER_CATEGORY。fit では 0（ヒント非表示用） */
    minRequiredWidthPx: number;
} {
    const layout = options?.layout ?? 'expand';
    const scrollRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const measure = () => {
            setContainerWidth(Math.round(el.getBoundingClientRect().width));
        };
        measure();
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const minBarWidthTotal = Math.max(0, labelCount) * BAR_CHART_MIN_PX_PER_CATEGORY;
    const minRequiredWidthPx = layout === 'expand' ? minBarWidthTotal : 0;

    const innerWidthPx =
        containerWidth > 0
            ? layout === 'fit'
                ? containerWidth
                : Math.max(containerWidth, minBarWidthTotal)
            : Math.max(minBarWidthTotal, 1);

    return { scrollRef, innerWidthPx, containerWidth, minRequiredWidthPx };
}
